import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check, ArrowRight, Loader2, Clock, X, AlertTriangle,
  Users, HardDrive, MessageSquare, ShieldCheck,
} from 'lucide-react';
import { subscriptionApi } from '@/lib/api';

interface Plan {
  slug:          string;
  name:          string;
  tagline:       string;
  price_monthly: number;
  max_users:     number;
  storage_gb:    number;
  sms_monthly:   number;
  wa_monthly:    number;
  features:      string[];
  sort_order:    number;
}

interface Subscription {
  plan:               string;
  planName:           string;
  tagline:            string;
  status:             string;
  billingCycle:       string;
  amountPaise:        number;
  trialEndsAt:        string | null;
  currentPeriodEnd:   string | null;
  planFeatures:       string[];
}

interface PendingRequest {
  id:         string;
  from_plan:  string;
  to_plan:    string;
  status:     string;
  note:       string | null;
  admin_note: string | null;
  created_at: string;
}

function paise(p: number) {
  return `₹${(p / 100).toLocaleString('en-IN')}`;
}

function fmtDate(d: string | null) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return d; }
}

const PLAN_ORDER = ['base', 'plus', 'pro'];

// ── Upgrade request modal ─────────────────────────────────────────
function UpgradeModal({
  currentPlan, targetPlan, onClose,
}: { currentPlan: string; targetPlan: Plan; onClose: () => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');

  const mut = useMutation({
    mutationFn: () => subscriptionApi.requestUpgrade(targetPlan.slug, note || undefined),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-subscription'] }); onClose(); },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-orange/10 flex items-center justify-center">
              <ArrowRight size={16} className="text-orange"/>
            </div>
            <h2 className="font-semibold text-navy">Request Plan Upgrade</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-navy"><X size={18}/></button>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-3 justify-between">
            <div className="text-center">
              <p className="text-xs text-gray-400">Current</p>
              <p className="font-semibold text-navy capitalize">{currentPlan}</p>
            </div>
            <ArrowRight size={16} className="text-orange"/>
            <div className="text-center">
              <p className="text-xs text-gray-400">Upgrading to</p>
              <p className="font-semibold text-orange">{targetPlan.name}</p>
              <p className="text-xs text-gray-400 mt-0.5">{paise(targetPlan.price_monthly)}/mo</p>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Message for our team <span className="text-gray-300">(optional)</span>
          </label>
          <textarea
            rows={3}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="We need IPD and pathology for our expansion…"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none
                       focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
          />
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-xs text-blue-700 mb-5">
          Our team will review and activate the upgrade within 1 business day. You will be notified via email.
        </div>

        {mut.isError && (
          <p className="text-clinical-danger text-sm mb-3">
            {(mut.error as any)?.response?.data?.error ?? 'Request failed'}
          </p>
        )}

        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-500 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="flex-1 py-2 rounded-lg bg-orange text-navy text-sm font-semibold
                       hover:bg-orange-dark disabled:opacity-40 flex items-center justify-center gap-2">
            {mut.isPending && <Loader2 size={14} className="animate-spin"/>}
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Plan card ─────────────────────────────────────────────────────
function PlanCard({
  plan, isCurrent, canUpgrade, hasPending, onUpgrade,
}: {
  plan:       Plan;
  isCurrent:  boolean;
  canUpgrade: boolean;
  hasPending: boolean;
  onUpgrade:  (p: Plan) => void;
}) {
  return (
    <div className={`relative rounded-2xl border-2 p-6 flex flex-col
                     ${isCurrent ? 'border-orange bg-orange/5' : 'border-gray-100 bg-white'}`}>
      {isCurrent && (
        <div className="absolute -top-3 left-4 bg-orange text-navy text-xs font-bold px-3 py-1 rounded-full">
          Current Plan
        </div>
      )}

      <div className="mb-4">
        <h3 className="font-bold text-navy text-lg">{plan.name}</h3>
        <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{plan.tagline}</p>
      </div>

      <div className="mb-4">
        <span className="text-3xl font-bold text-navy">{paise(plan.price_monthly)}</span>
        <span className="text-sm text-gray-400"> / month</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Chip icon={<Users size={11}/>}
          label={plan.max_users >= 999 ? 'Unlimited users' : `${plan.max_users} users`}/>
        <Chip icon={<HardDrive size={11}/>} label={`${plan.storage_gb} GB`}/>
        {plan.sms_monthly > 0 && (
          <Chip icon={<MessageSquare size={11}/>} label={`${plan.sms_monthly.toLocaleString()} SMS`}/>
        )}
      </div>

      <ul className="flex-1 space-y-1.5 mb-6">
        {(Array.isArray(plan.features) ? plan.features : []).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
            <Check size={11} className="text-clinical-success mt-0.5 flex-shrink-0"/>
            {f}
          </li>
        ))}
      </ul>

      {!isCurrent && canUpgrade && (
        <button
          onClick={() => onUpgrade(plan)}
          disabled={hasPending}
          className="w-full py-2.5 rounded-xl bg-orange text-navy text-sm font-semibold
                     hover:bg-orange-dark disabled:opacity-40 transition-colors">
          {hasPending ? 'Request pending…' : `Upgrade to ${plan.name}`}
        </button>
      )}
      {isCurrent && (
        <div className="flex items-center justify-center gap-2 py-2.5 text-orange text-sm font-medium">
          <ShieldCheck size={15}/> Active Plan
        </div>
      )}
    </div>
  );
}

function Chip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-navy/5 text-navy px-2 py-1 rounded-full">
      {icon}{label}
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function SubscriptionPage() {
  const qc = useQueryClient();
  const [upgradeTarget, setUpgradeTarget] = useState<Plan | null>(null);
  const cancelMut = useMutation({
    mutationFn: subscriptionApi.cancelUpgrade,
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['my-subscription'] }),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-subscription'],
    queryFn:  () => subscriptionApi.mine().then(r => r.data),
  });

  if (isLoading) {
    return <div className="flex justify-center py-24"><Loader2 size={28} className="animate-spin text-orange"/></div>;
  }
  if (error) {
    return (
      <div className="p-6 text-center text-gray-400">
        <AlertTriangle size={28} className="mx-auto mb-2 text-orange"/>
        <p>Could not load subscription details</p>
      </div>
    );
  }

  const { subscription, allPlans, pendingRequest } = (data ?? {}) as {
    subscription:   Subscription | null;
    allPlans:       Plan[];
    pendingRequest: PendingRequest | null;
  };

  const safeAllPlans = allPlans ?? [];

  // Only show upgrade options (same or higher plan rank)
  const currentIdx = subscription ? PLAN_ORDER.indexOf(subscription.plan) : -1;

  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-navy">Subscription</h1>
        <p className="text-sm text-gray-400 mt-0.5">Your current plan and available upgrades</p>
      </div>

      {/* Current plan summary strip */}
      {subscription ? (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 flex flex-wrap items-center gap-6">
          <div>
            <p className="text-xs text-gray-400">Current Plan</p>
            <p className="text-lg font-bold text-navy">{subscription.planName}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Status</p>
            <p className="text-sm font-semibold capitalize text-emerald-600">{subscription.status}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Monthly charge</p>
            <p className="text-sm font-semibold text-navy">{paise(subscription.amountPaise)}/mo</p>
          </div>
          {subscription.currentPeriodEnd && (
            <div>
              <p className="text-xs text-gray-400">Current period ends</p>
              <p className="text-sm font-semibold text-navy">{fmtDate(subscription.currentPeriodEnd)}</p>
            </div>
          )}
          {subscription.trialEndsAt && (
            <div className="ml-auto">
              <span className="text-xs bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1 rounded-full">
                Trial ends {fmtDate(subscription.trialEndsAt)}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6 flex items-center gap-3">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0"/>
          <p className="text-sm text-amber-800">No active subscription found. Please contact BBS support to set up your plan.</p>
        </div>
      )}

      {/* Pending upgrade request banner */}
      {pendingRequest && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-6
                        flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Clock size={18} className="text-amber-600 flex-shrink-0"/>
            <div>
              <p className="text-sm font-semibold text-amber-800">Upgrade request pending</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Requested on {fmtDate(pendingRequest.created_at)} ·{' '}
                <span className="font-medium capitalize">{pendingRequest.from_plan}</span>
                {' '}<ArrowRight size={11} className="inline"/>{' '}
                <span className="font-medium capitalize">{pendingRequest.to_plan}</span>
              </p>
            </div>
          </div>
          <button
            onClick={() => cancelMut.mutate()}
            disabled={cancelMut.isPending}
            className="text-xs text-amber-700 border border-amber-300 px-3 py-1.5 rounded-lg
                       hover:bg-amber-100 transition-colors flex items-center gap-1">
            {cancelMut.isPending ? <Loader2 size={11} className="animate-spin"/> : <X size={11}/>}
            Cancel request
          </button>
        </div>
      )}

      {/* Plan comparison grid */}
      <div className={`grid gap-5 ${safeAllPlans.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {safeAllPlans.map(plan => {
          const planIdx    = PLAN_ORDER.indexOf(plan.slug);
          const canUpgrade = planIdx > currentIdx;
          return (
            <PlanCard
              key={plan.slug}
              plan={plan}
              isCurrent={!!subscription && plan.slug === subscription.plan}
              canUpgrade={canUpgrade}
              hasPending={!!pendingRequest}
              onUpgrade={setUpgradeTarget}
            />
          );
        })}
      </div>

      {/* Upgrade request modal */}
      {upgradeTarget && subscription && (
        <UpgradeModal
          currentPlan={subscription.plan}
          targetPlan={upgradeTarget}
          onClose={() => setUpgradeTarget(null)}
        />
      )}
    </div>
  );
}
